import {
  EXTERIOR_PAYOUT_PERCENTAGE_MAX,
  SUBDIVISION_EXTERIOR_PAYOUT_OFFSET_MIN,
  SubdivisionOverrideTypeEnum,
  exteriorPayoutError,
  toHundredths,
} from '@vatos-pas/common';
import { optionalPercentageErrors } from './requiredPercentageErrors';

export type ExteriorPayoutField =
  | 'exteriorOnePercentage'
  | 'exteriorTwoPercentage';

export type ExteriorPayoutPair = Partial<
  Record<ExteriorPayoutField, number | string | null>
>;

export type ExteriorPayoutErrors = Partial<
  Record<ExteriorPayoutField, string[]>
>;

const LABELS: Record<ExteriorPayoutField, string> = {
  exteriorOnePercentage: 'Exterior One',
  exteriorTwoPercentage: 'Exterior Two',
};

const FIELDS = Object.keys(LABELS) as ExteriorPayoutField[];

const toPercentage = (value: number | string | null | undefined) =>
  toHundredths(value) === null ? null : Number(value);

const offsetErrors = (
  value: number | null,
  label: string,
): string[] | undefined => {
  if (value === null) {
    return undefined;
  }
  if (value < SUBDIVISION_EXTERIOR_PAYOUT_OFFSET_MIN) {
    return [
      `The ${label} must be at least ${SUBDIVISION_EXTERIOR_PAYOUT_OFFSET_MIN}`,
    ];
  }
  if (value > EXTERIOR_PAYOUT_PERCENTAGE_MAX) {
    return [`The ${label} must be ${EXTERIOR_PAYOUT_PERCENTAGE_MAX} or less`];
  }
  return undefined;
};

const legErrors = (
  pair: ExteriorPayoutPair,
  check: (value: number | null, label: string) => string[] | undefined,
): ExteriorPayoutErrors => {
  const errors: ExteriorPayoutErrors = {};
  FIELDS.forEach(field => {
    const messages = check(toPercentage(pair[field]), LABELS[field]);
    if (messages) {
      errors[field] = messages;
    }
  });
  return errors;
};

const pairErrorUnderExteriorOne = (
  exteriorOne: number | null,
  exteriorTwo: number | null,
): ExteriorPayoutErrors => {
  const message = exteriorPayoutError(exteriorOne, exteriorTwo);
  return message ? { exteriorOnePercentage: [message] } : {};
};

const hasErrors = (errors: ExteriorPayoutErrors) =>
  Object.keys(errors).length > 0;

export const regionExteriorPayoutErrors = (
  pair: ExteriorPayoutPair,
): ExteriorPayoutErrors => {
  const errors = legErrors(pair, optionalPercentageErrors);
  if (hasErrors(errors)) {
    return errors;
  }
  return pairErrorUnderExteriorOne(
    toHundredths(pair.exteriorOnePercentage),
    toHundredths(pair.exteriorTwoPercentage),
  );
};

export const subdivisionExteriorPayoutErrors = ({
  overrideType,
  subdivision,
  region,
}: {
  overrideType: SubdivisionOverrideTypeEnum | null | undefined;
  subdivision: ExteriorPayoutPair;
  region: ExteriorPayoutPair | null;
}): ExteriorPayoutErrors => {
  if (overrideType !== SubdivisionOverrideTypeEnum.OFFSET) {
    return regionExteriorPayoutErrors(subdivision);
  }
  const errors = legErrors(subdivision, offsetErrors);
  if (hasErrors(errors) || !region) {
    return errors;
  }
  const resolve = (field: ExteriorPayoutField) =>
    (toHundredths(subdivision[field]) ?? 0) +
    (toHundredths(region[field]) ?? 0);
  return pairErrorUnderExteriorOne(
    resolve('exteriorOnePercentage'),
    resolve('exteriorTwoPercentage'),
  );
};
